/**
 * Copyright © 2022-2023 Delicious AI, LLC
 *
 * @author Wade Wilkey <wade.w.wilkey@gmail.com>
 */

import {
  DisplayElements,
  PicOSAllPrioritiesComplianceQuery,
  PicOSAllPrioritiesComplianceStatsQuery,
  PicOSAllPrioritiesComplianceStatsQueryVariables,
  PicOSAllPrioritiesComplianceQueryVariables,
  PicOSAllPrioritiesCompliance_picosAllPrioritiesCompliance_results,
  PICOS_ALL_PRIORITIES_COMPLIANCE,
  PICOS_ALL_PRIORITIES_COMPLIANCE_STATS,
} from '@dai/graphql'
import {
  useDebouncedItemQuery,
  usePagination,
  PicOSReportContext,
} from '@dai/web-components'
import { PicOSReportHelpers } from 'picos/helpers/picos-reports.helpers'
import { PicOSHelpers } from 'picos/helpers/picos.helpers'
import { useContext, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

const useAllPrioritiesComplianceReport = () => {
  const history = useHistory()
  const { selectedDateRange, setSelectedPriority } = useContext(
    PicOSReportContext,
  )
  const {
    offset,
    limit,
    handleOffset: { goToPage },
  } = usePagination({ pageSize: 20 })

  const OverviewQuery = useDebouncedItemQuery<
    PicOSAllPrioritiesComplianceQuery,
    PicOSAllPrioritiesComplianceQueryVariables
  >({
    queryStr: PICOS_ALL_PRIORITIES_COMPLIANCE,
    options: {
      variables: {
        input: {
          ...PicOSReportHelpers.dateRangeToDateInput(selectedDateRange),
          offset,
          limit,
        },
      },
    },
    queryOnMount: true,
  })

  const StatsQuery = useDebouncedItemQuery<
    PicOSAllPrioritiesComplianceStatsQuery,
    PicOSAllPrioritiesComplianceStatsQueryVariables
  >({
    queryStr: PICOS_ALL_PRIORITIES_COMPLIANCE_STATS,
    options: {
      variables: {
        input: {
          ...PicOSReportHelpers.dateRangeToDateInput(selectedDateRange),
        },
      },
    },
    queryOnMount: true,
  })

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = event.target.value
    OverviewQuery.setItemQuery(searchTerm)
    StatsQuery.setItemQuery(searchTerm)
  }

  const getPriorityDetails = (
    row: PicOSAllPrioritiesCompliance_picosAllPrioritiesCompliance_results,
  ) => ({
    priority: row.priority.priority,
    name: row.priority.name,
    displayElements: DisplayElements[row.priority.displayElements],
    mandateType: row.priority.mandateType,
    imageUrl: row.priority.image?.url || null,
  })

  const handleSelectRow = (
    row: PicOSAllPrioritiesCompliance_picosAllPrioritiesCompliance_results,
  ) => {
    const priorityDetails = getPriorityDetails(row)
    setSelectedPriority(priorityDetails)
    history.push(`priorities/${row.id}`)
  }

  useEffect(() => {
    const variables = {
      variables: {
        input: {
          ...PicOSReportHelpers.dateRangeToDateInput(selectedDateRange),
          priorityName: OverviewQuery.debouncedQuery || undefined,
          offset,
        },
      },
    }
    OverviewQuery.lazyQuery.query(variables)
    StatsQuery.lazyQuery.query(variables)
  }, [OverviewQuery.debouncedQuery, selectedDateRange, offset])

  const overviewQuery = OverviewQuery.lazyQuery.meta
  const statsQuery = StatsQuery.lazyQuery.meta

  const statItems = PicOSReportHelpers.getStatItems(
    ['compliance', 'priorities', 'cost'],
    statsQuery.data?.picosAllPrioritiesStats,
  )

  return {
    data: {
      title: 'All Priorities',
      complianceResults:
        overviewQuery.data?.picosAllPrioritiesCompliance.results || [],
      statItems,
    },
    state: {
      limit,
      loadingStats: !!statsQuery.loading,
      loadingCompliance: !!overviewQuery.loading,
    },
    handlers: {
      getPriorityDetails,
      goToPage,
      handleSearch,
      handleSelectRow,
    },
  }
}

export default useAllPrioritiesComplianceReport
